import { AsyncPipe, CurrencyPipe, NgClass, NgFor, NgIf } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { CardComponent } from '@components/card/card.component';
import { PaymentCardFormComponent } from '@components/consultation-request/payment-card-form/payment-card-form.component';
import { PaymentSubmitButtonComponent } from '@components/consultation-request/payment-submit-button/payment-submit-button.component';
import { DoxyPepStdUpsell } from '@data/tests';
import { ConsultationTreatmentTypes } from '@enums/consultation-treatment-types';
import { PaymentTypes } from '@enums/payment-types';
import { ConfigService, Payment } from '@medology/ngx-payment';
import { BasicConsultationRequestRequest } from '@models/consultation-request/basic-consultation-request-request';
import { BasicConsultationRequestResponse } from '@models/consultation-request/basic-consultation-request-response';
import { DoxyPepFaq } from '@models/doxy-pep-faq';
import { DoxyPepResponse } from '@models/doxy-pep-response';
import { OrderResponse } from '@models/order-response';
import { OrderUpsell } from '@models/order-upsell';
import { PaymentRechargeBraintreeStoreRequest } from '@models/requests/payment-recharge-briantree-store-request.model';
import { TransactionResponse } from '@models/transaction-response';
import { ImageFallback } from '@pipes/image-fallback';
import { ConsultationRequestService } from '@services/consultation-request.service';
import { DataLayerService } from '@services/data-layer.service';
import { DoxyPepService } from '@services/doxy-pep.service';
import { ErrorHandlerService } from '@services/error-handler.service';
import { ErrorLoggingService } from '@services/error-logging.service';
import { LoadingService } from '@services/loading.service';
import { NavigationService } from '@services/navigation.service';
import { OrderService } from '@services/order.service';
import { OrderPaymentService } from '@services/order-payment.service';
import { provideDefaultPaymentConfig } from '@services/payment.service';
import { PaymentService } from '@services/payment.service';
import { StorageService } from '@services/storage.service';
import { catchError, forkJoin, Observable, of, switchMap } from 'rxjs';

@Component({
  selector: 'app-doxy-pep',
  templateUrl: './doxy-pep.component.html',
  styleUrls: ['./doxy-pep.component.scss'],
  imports: [
    NgIf,
    CardComponent,
    NgFor,
    NgClass,
    PaymentCardFormComponent,
    PaymentSubmitButtonComponent,
    AsyncPipe,
    CurrencyPipe,
    ImageFallback,
  ],
  providers: [provideDefaultPaymentConfig()],
})
export class DoxyPEPComponent implements OnInit {
  @ViewChild('paymentCardForm', { static: false }) public paymentCardFormComponent: PaymentCardFormComponent;

  public doxyPepContent: DoxyPepResponse;
  public shouldExpand: boolean = false;
  public showPaymentInformation: boolean = false;
  public submissionErrors: string[] = [];
  public processing: boolean = false;
  public payments: Payment[] = [];

  public readonly doxyPepTest: OrderUpsell = DoxyPepStdUpsell;

  private router = inject(Router);
  private storageService = inject(StorageService);
  private orderService = inject(OrderService);
  private errorHandlerService = inject(ErrorHandlerService);
  private errorLoggingService = inject(ErrorLoggingService);
  private doxyPepService = inject(DoxyPepService);
  private loadingService = inject(LoadingService);
  private consultationRequestService = inject(ConsultationRequestService);
  private datalayerService = inject(DataLayerService);
  private orderPaymentService = inject(OrderPaymentService);
  private paymentService = inject(PaymentService);
  private paymentConfigService = inject(ConfigService);
  private navigationService = inject(NavigationService);

  private rechargeData: { recharge?: { vault_id?: string } } = null;

  /**
   * Gets the price of the DoxyPep test.
   */
  public get doxyPepPrice(): number {
    return Number(this.doxyPepContent.price);
  }

  /**
   * Gets the order details.
   */
  private get order(): OrderResponse {
    return this.storageService.order;
  }

  /**
   * Initializes the component.
   */
  public ngOnInit(): void {
    this.getDoxyPepContent();
    this.paymentService.setGiftCardConfig(this.paymentConfigService);

    if (this.storageService.paymentType !== PaymentTypes.PayLater) {
      this.getRecharge();
    }
  }

  /**
   * Toggles the content of the DoxyPep content.
   */
  public toggleContent(): void {
    this.shouldExpand = !this.shouldExpand;
  }

  /**
   * Toggles the content of the DoxyPep FAQ.
   *
   * @param { DoxyPepFaq } faq the FAQ to toggle
   */
  public toggleFAQ(faq: DoxyPepFaq): void {
    faq.isExpanded = !faq.isExpanded;
  }

  /**
   * Handles payment selection from the payment form.
   *
   * @param {Payment[]} payments the selected payments
   */
  public onPaymentsSelected(payments: Payment[]): void {
    this.payments = payments;
    this.submissionErrors = [];
  }

  /**
   * Determine workflow to perform recharge or manual payment.
   */
  public submit(): void {
    this.submissionErrors = [];
    this.processing = true;

    if (!this.rechargeData?.recharge?.vault_id && !this.showPaymentInformation) {
      this.showPaymentInformation = true;
      this.processing = false;

      return;
    }

    if (this.showPaymentInformation && !this.paymentCardFormComponent?.validateForm()) {
      this.processing = false;

      return;
    }

    this.processDoxyPepOrder();
  }

  /**
   * Navigates to the next page in the order flow.
   */
  public navigateToNextPage(): void {
    this.router.navigateByUrl(this.navigationService.getNextNavigationUrl());
  }

  /**
   * Processes DoxyPEP order: payment, test addition, and consultation creation.
   */
  private processDoxyPepOrder(): void {
    const orderId = this.order.transaction_id;
    const hash = this.order.hash;

    this.processPayment()
      .pipe(switchMap(() => forkJoin([this.addTest(orderId, hash), this.createConsultationRequest(orderId, hash)])))
      .subscribe({
        next: () => this.completeDoxyPepOrder(orderId),
        error: (error) => this.handlePaymentError(error),
      });
  }

  /**
   * Fetches order details including recharge info using hash authentication.
   */
  private getRecharge(): void {
    const orderId = this.order.transaction_id;
    const hash = this.order.hash;

    this.consultationRequestService.getOrderDetails(orderId, hash).subscribe({
      next: (response) => {
        if (response.body?.recharge) {
          this.rechargeData = response.body;
        }
      },
    });
  }

  /**
   * Process payment for DoxyPEP test, handling recharge and gift card flows.
   */
  private processPayment(): Observable<TransactionResponse> {
    if (!this.showPaymentInformation && this.rechargeData?.recharge?.vault_id) {
      return this.orderPaymentService.store(
        new PaymentRechargeBraintreeStoreRequest(
          this.order.transaction_id,
          this.doxyPepTest.price,
          this.rechargeData.recharge.vault_id,
        ),
        this.order.hash,
      );
    }

    const [initialRequest, followUpRequest] = this.paymentService.getPaymentRequests(
      this.payments,
      this.doxyPepTest.price,
      this.order.transaction_id,
      `${this.order.first_name} ${this.order.last_name}`,
    );

    return this.orderPaymentService
      .store(initialRequest, this.order.hash)
      .pipe(
        switchMap((response) =>
          followUpRequest
            ? this.orderPaymentService
                .store(followUpRequest, this.order.hash)
                .pipe(catchError((error) => this.orderService.swallowError(error)))
            : of(response),
        ),
      );
  }

  /**
   * Add DoxyPEP test to order billing.
   *
   * @param {string} orderId the order transaction ID
   * @param {string} hash the order hash for authentication
   */
  private addTest(orderId: string, hash: string): Observable<any> {
    return this.orderService.addTestToOrder(this.doxyPepTest.id, orderId, hash).pipe(
      catchError((error) => {
        this.errorLoggingService.logError(error);

        return of(null);
      }),
    );
  }

  /**
   * Create basic std_prevention consultation request.
   *
   * @param {string} orderId the order transaction ID
   * @param {string} hash the order hash for authentication
   */
  private createConsultationRequest(
    orderId: string,
    hash: string,
  ): Observable<BasicConsultationRequestResponse | null> {
    const consultationRequest = new BasicConsultationRequestRequest(
      orderId,
      ConsultationTreatmentTypes.StdPrevention,
      this.order.first_name,
      this.order.last_name,
      this.order.date_of_birth,
      this.order.email,
    );

    return this.consultationRequestService.postBasicConsultationRequest(consultationRequest, hash, orderId).pipe(
      catchError((error) => {
        this.errorLoggingService.logError(error);

        return of(null);
      }),
    );
  }

  /**
   * Completes DoxyPEP order after successful payment.
   *
   * @param { string } orderId the order transaction ID
   */
  private completeDoxyPepOrder(orderId: string): void {
    this.orderService.addUpsellTestToStorage(this.doxyPepTest);
    this.orderService.addUpsellPriceToTotal(this.doxyPepTest.upsell_price);
    this.datalayerService.addConsultationRequestPurchaseToDataLayer(
      {
        line_items: [
          {
            name: this.doxyPepTest.name,
            price: this.doxyPepTest.price,
            type: 'consultation',
          },
        ],
        payment_amount: this.doxyPepTest.price,
      },
      ConsultationTreatmentTypes.StdPrevention,
      orderId,
    );

    this.processing = false;
    this.navigateToNextPage();
  }

  /**
   * Handles payment errors.
   *
   * @param { HttpErrorResponse } error The payment error response object
   */
  private handlePaymentError(error: HttpErrorResponse): void {
    this.processing = false;

    if (this.showPaymentInformation) {
      this.submissionErrors = this.errorHandlerService.handleResponseError(error);

      return;
    }

    this.showPaymentInformation = true;
  }

  /**
   * Fetches the content for the DoxyPep component.
   */
  private getDoxyPepContent(): void {
    this.loadingService.toggleLoader(true);
    this.doxyPepService.getDoxyPepContent().subscribe({
      next: (response) => {
        this.doxyPepContent = response;
        this.doxyPepContent.faq_content.faqs.map((faq) => (faq.isExpanded = false));
        this.loadingService.toggleLoader(false);
      },
      error: () => {
        this.loadingService.toggleLoader(false);
        setTimeout(() => this.navigateToNextPage(), 0);
      },
    });
  }
}
